import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import track from 'utils/track';
import { getAnnouncementHeadingId } from 'utils/announcement/getHeadingId';
import Box from 'components/Box';
import Flex from 'components/Flex';
import Card from 'components/Card';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import TooltipButton from 'components/TooltipButton';
import Icon from 'components/Icon';
import { SpinnerDot } from 'components/icons';
import Flag from 'components/Flag';
import VisuallyHidden from 'components/VisuallyHidden';
import useSearchParameter from 'hooks/useSearchParameter';
import { announcementQueryKeys, useUpdateUserAnnouncementState, useFetchAnnouncement } from 'actions/announcements';
import PlatformAnnouncementModal from 'screens/PlatformAnnouncementModal';
function AnnouncementBanner(_ref) {
  var announcement = _ref.announcement,
    isDismissable = _ref.isDismissable;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var queryClient = useQueryClient();
  var headingId = getAnnouncementHeadingId(announcement.id);
  var readLabelId = "".concat(headingId, "-read-label");
  var _useSearchParameter = useSearchParameter('announcement-modal'),
    _useSearchParameter2 = _slicedToArray(_useSearchParameter, 2),
    selectedAnnouncementId = _useSearchParameter2[0],
    setSelectedAnnouncementId = _useSearchParameter2[1];
  var hasAnnouncementModal = selectedAnnouncementId === announcement.id;
  var _useFetchAnnouncement = useFetchAnnouncement({
      announcementId: announcement.id,
      enabled: hasAnnouncementModal
    }),
    selectedAnnouncement = _useFetchAnnouncement.data,
    isLoadingSelectedAnnouncement = _useFetchAnnouncement.isLoading;
  var _useUpdateUserAnnounc = useUpdateUserAnnouncementState({
      announcementId: announcement.id
    }),
    updateUserAnnouncementState = _useUpdateUserAnnounc.updateUserAnnouncementState;
  function handleLinkClick() {
    // Mark as read only first time user clicks on the announcement
    if (!announcement.isRead) {
      updateUserAnnouncementState({
        is_read: true
      });
      track('announcement', 'read', announcement.id);
    }
    setSelectedAnnouncementId(announcement.id);
  }
  function handleDismiss() {
    updateUserAnnouncementState({
      is_dismissed: true
    });
    track('announcement', 'dismissed', announcement.id);
  }
  function closeModal() {
    queryClient.invalidateQueries(announcementQueryKeys.announcements);
    setSelectedAnnouncementId(undefined);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    variant: "clickable",
    px: "m",
    py: 12,
    position: "relative"
  }, !announcement.isRead && /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "links",
    position: "absolute",
    left: true,
    top: true,
    bottom: true,
    width: 4
  }), /*#__PURE__*/React.createElement(Flag, {
    align: "top",
    token: isLoadingSelectedAnnouncement ? /*#__PURE__*/React.createElement(SpinnerDot, {
      vAlign: true,
      color: "links"
    }) : /*#__PURE__*/React.createElement(Icon, {
      vAlign: true,
      name: "megaphone",
      color: "links"
    })
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "row",
    wrap: true,
    gap: "xs",
    mr: "s"
  }, /*#__PURE__*/React.createElement(TextLink, {
    id: headingId,
    bold: true,
    as: "button",
    onClick: handleLinkClick,
    "aria-describedby": readLabelId
  }, t(announcement.title), /*#__PURE__*/React.createElement(Box
  // Extend clickable area across whole banner
  , {
    fillParent: true
  })), /*#__PURE__*/React.createElement(Text, null, t(announcement.callOut)), !!announcement.isRead && /*#__PURE__*/React.createElement(VisuallyHidden, {
    id: readLabelId
  }, ' ', t('state_read_brackets')))), isDismissable && /*#__PURE__*/React.createElement(TooltipButton, {
    iconOnly: true,
    icon: "x",
    size: "medium",
    variant: "transparent",
    onClick: handleDismiss,
    position: "absolute",
    right: "xxs",
    top: "xs"
  }, t('actions_dismiss'))), hasAnnouncementModal && selectedAnnouncement && /*#__PURE__*/React.createElement(PlatformAnnouncementModal, {
    announcement: selectedAnnouncement,
    onClose: closeModal
  }));
}
export default AnnouncementBanner;